import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFiletreeCore, FileClickEvent } from "./useFiletreeCore";
import {
  searchFiles,
  fetchFiles,
  FileEntry,
  FolderEntry,
  Drive,
  fileToRenderable,
} from "./fetchFiles";
import { isSelectionBlockedFile } from "./rendering";
import { FolderSelection, PendingFileMetadata } from "./features/selection";
import { computeFolderSplit } from "./selectionSplit";
import { expandSelectionsToFileIds } from "./expandSelections";
import { SearchBar, SearchBarHandle } from "./SearchBar";
import { SearchResultsView } from "./SearchResultsView";
import { createActionContext } from "./actions";
import React from "react";
import { Eye } from "lucide-react";
import { useMockTreeExpansion } from "./useMockTreeExpansion";

/** Whether a search result may be picked: not still processing and not
 *  locked. A failed file may (#13887). */
const isSelectableResult = (file: FileEntry): boolean =>
  !isSelectionBlockedFile(fileToRenderable(file));

export type { FolderSelection };

/**
 * Options for the useFileSelection hook.
 * Designed to be minimal and hard to misuse.
 */
export interface UseFileSelectionOptions {
  /** Optional: filter to a specific drive */
  driveId?: string;
  /** Optional: show files across all drives (only works when driveId is undefined) */
  showAcrossDrives?: boolean;
  /** Optional: list of drives for display names in across-drives mode */
  drives?: Drive[];
  /** Optional: initial file IDs to select (applied once when tree loads) */
  initialFileIds?: string[];
  /** Optional: initial folder paths to select (as string arrays) */
  initialFolderPaths?: string[][];
  /** Optional: callback when a file is clicked. If not provided, opens file in viewer. */
  onFileClick?: (event: FileClickEvent) => void;
  /** Optional: blob kinds to exclude from file listings and search results */
  excludeBlobKinds?: string[];
  /**
   * Optional: whether the tree may load its contents. A host mounted before
   * it is shown (a closed picker) passes false and loads nothing until it
   * flips to true (#12239). Defaults to true.
   */
  loadTree?: boolean;
}

export type { FileClickEvent };

/** Selected file metadata */
export interface SelectedFileInfo {
  id: string;
  name: string;
  type: string;
}

/**
 * Return type for useFileSelection hook.
 * Provides a clear, minimal API for file selection.
 */
export interface UseFileSelectionReturn {
  /** Combined view with search bar + tree/results (most common use case) */
  tree: React.ReactNode;
  /** Just the search bar component (for flexible positioning) */
  searchBar: React.ReactNode;
  /** Just the tree/search results content without search bar */
  treeContent: React.ReactNode;
  /** Whether search is currently active (3+ characters) */
  isSearchActive: boolean;
  /** Array of selected file IDs */
  selectedFileIds: string[];
  /** Array of selected files with metadata (id, name, type) */
  selectedFiles: SelectedFileInfo[];
  /** Array of selected folder selections with drive ID and path */
  selectedFolderSelections: FolderSelection[];
  /** Whether any items are selected */
  hasSelection: boolean;
  /** Number of selected files */
  selectedFileCount: number;
  /** Number of selected folders (for display purposes) */
  selectedFolderCount: number;
  /** Clear all selections */
  clearSelection: () => void;
  /** Programmatically set the selection (replaces current selection) */
  setSelection: (fileIds: string[], folderPaths?: string[][]) => void;
  /** Select additional files (merges with current selection) */
  selectFiles: (
    fileIds: string[],
    metadata?: Map<string, PendingFileMetadata>
  ) => void;
  /** Available drives (populated after tree loads) */
  drives: { _id: string; name: string }[];
  /** Whether drives feature is enabled */
  drivesEnabled: boolean;
  /** Whether to show only selected files (for filtering) */
  showSelectedOnly: boolean;
  /** Toggle or set showSelectedOnly state */
  setShowSelectedOnly: React.Dispatch<React.SetStateAction<boolean>>;
  /** Pre-built selection controls component with "Show selected"/"Clear all" buttons and count */
  selectionControls: React.ReactNode;
  /** Put the picker in its opening state: the given selection, the tree
   *  view with that selection pinned above it, an empty search, and the
   *  one-time search auto-switch armed again. Call when the modal opens. */
  prepareForOpen: (fileIds: string[], folderPaths: string[][]) => void;
}

/** A folder path as a map key, the whole path at once. */
const folderKey = (path: string[]): string => JSON.stringify(path);

/**
 * A simple, focused hook for file selection scenarios.
 *
 * This is the recommended hook for file selection modals and similar use cases.
 * It provides a minimal API that's hard to misuse:
 *
 * - Checkboxes are always enabled (that's the point of selection)
 * - Returns concrete file IDs and folder paths (not internal datum references)
 * - Includes convenience getters for common checks
 * - Supports initial selection and programmatic updates
 *
 * @example
 * ```tsx
 * const { tree, selectedFileIds, hasSelection, clearSelection, setSelection } = useFileSelection({
 *   driveId: currentDriveId,
 *   initialFileIds: preSelectedIds,
 * });
 *
 * // Restore selection on cancel
 * const handleCancel = () => {
 *   setSelection(originalFileIds);
 *   onClose();
 * };
 *
 * return (
 *   <div>
 *     {tree}
 *     <button onClick={clearSelection} disabled={!hasSelection}>
 *       Clear Selection
 *     </button>
 *     <button onClick={() => onConfirm(selectedFileIds)} disabled={!hasSelection}>
 *       Confirm ({selectedFileIds.length} files)
 *     </button>
 *   </div>
 * );
 * ```
 */
export const useFileSelection = (
  options: UseFileSelectionOptions = {}
): UseFileSelectionReturn => {
  const {
    driveId,
    showAcrossDrives = true, // Default to showing all drives for file selection
    initialFileIds = [],
    initialFolderPaths = [],
    onFileClick,
    excludeBlobKinds,
    loadTree = true,
  } = options;

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<FileEntry[]>([]);
  const [searchFolderResults, setSearchFolderResults] = useState<FolderEntry[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const isSearchActive = searchTerm.length >= 3;

  // Selection display state
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const searchBarRef = useRef<SearchBarHandle>(null);

  // Track whether we've auto-switched from "Show Selected" to "Show All"
  // on the first search. This fires at most once per modal open.
  const hasAutoSwitchedRef = useRef(false);

  // The selection as it was when the picker opened, pinned above the tree
  // (#11207): the user sees what they already have without leaving the
  // default view. Files ticked afterwards are the tree's rows; a pinned
  // item unticked leaves the section, since only selected items render.
  const [pinned, setPinned] = useState<{
    fileIds: Set<string>;
    folderKeys: Set<string>;
  } | null>(null);

  // Sort state
  const [sortBy, setSortBy] = useState<"name" | "date">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSortChange = useCallback(
    (column: "name" | "date") => {
      if (sortBy === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(column);
        setSortDirection("asc");
      }
    },
    [sortBy]
  );

  // Use core hook with checkboxes always enabled
  const core = useFiletreeCore({
    driveId,
    showAcrossDrives,
    showCheckboxes: true, // Always enabled for selection
    sortBy,
    sortDirection,
    onSortChange: handleSortChange,
    onFileClick,
    excludeBlobKinds,
    // The picker's narrow frame gives the date's room to the name — a phone
    // dialog picking files has nothing to gain from a third column (#11742,
    // CD (b)).
    narrowDate: "hidden",
    loadTree,
  });

  // Build driveId -> driveName map for search results from loaded drives
  const driveIdToName = useMemo(
    () => new Map(core.drives.map((d) => [d._id, d.name])),
    [core.drives]
  );

  // This surface picks documents rather than acting on them, so its right-click
  // menu stays limited to inspecting and downloading a file — see PICKER_ACTIONS.
  //
  // Deliberately carries no path-shape flag. In show-selected mode this one
  // context serves rows of two different shapes at once: tree datums (which may
  // carry a synthetic drive prefix) alongside API-shaped pending files/folders.
  // No single `isAcrossDrivesMode` value is correct for both, so rather than
  // pick a wrong one, the allowlist admits only actions that never read the
  // path. Anything added to PICKER_ACTIONS that does read it has to resolve the
  // mixed-shape problem first.
  const pickerActionContext = useMemo(
    () =>
      createActionContext({
        refreshTree: () => {},
        driveId,
        isPicker: true,
      }),
    [driveId]
  );

  // Inline folder expansion for search results and show-selected mode
  const mockTree = useMockTreeExpansion(excludeBlobKinds);

  // Clear expansions when search term or show-selected mode changes
  useEffect(() => {
    mockTree.clearExpansions();
  }, [searchTerm, showSelectedOnly]);

  // Search effect
  useEffect(() => {
    if (!isSearchActive) {
      setSearchResults([]);
      setSearchFolderResults([]);
      return;
    }

    const abortController = new AbortController();
    setIsSearching(true);

    searchFiles({
      query: searchTerm,
      driveId,
      limit: 5000,
      signal: abortController.signal,
      excludeBlobKinds,
    })
      .then(({ files, folders }) => {
        if (!abortController.signal.aborted) {
          setSearchResults(files);
          setSearchFolderResults(folders);
          setIsSearching(false);
        }
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          console.error("Search failed:", error);
          setSearchResults([]);
          setSearchFolderResults([]);
          setIsSearching(false);
        }
      });

    return () => abortController.abort();
  }, [searchTerm, driveId, isSearchActive, excludeBlobKinds]);

  // Auto-switch from "Show Selected" to "Show All" on the first search.
  // Only fires once per modal open (re-armed by prepareForOpen).
  useEffect(() => {
    if (
      searchTerm.length > 0 &&
      showSelectedOnly &&
      !hasAutoSwitchedRef.current
    ) {
      hasAutoSwitchedRef.current = true;
      setShowSelectedOnly(false);
    }
  }, [searchTerm, showSelectedOnly]);

  // Convert folderSelections to folderPaths format for setSelection
  const folderSelectionsToFolderPaths = useCallback(
    (folderSelections: FolderSelection[]): string[][] => {
      return folderSelections.map((fs) =>
        fs.driveId ? [fs.driveId, ...fs.path] : fs.path
      );
    },
    []
  );

  // Check if a file is selected (for search results)
  const isFileSelected = useCallback(
    (fileId: string) => core.selection.fileIds.includes(fileId),
    [core.selection.fileIds]
  );

  // Check if a folder is selected (for search results)
  // Also returns true if any ancestor folder is selected
  const isFolderSelected = useCallback(
    (folder: FolderEntry) => {
      const folderPath = folder.driveId
        ? [folder.driveId, ...folder.filePath]
        : folder.filePath;
      const currentFolderPaths = folderSelectionsToFolderPaths(
        core.selection.folderSelections
      );
      return currentFolderPaths.some(
        (p) =>
          p.length <= folderPath.length &&
          p.every((seg, i) => folderPath[i] === seg)
      );
    },
    [core.selection.folderSelections, folderSelectionsToFolderPaths]
  );

  /**
   * Get a folder's direct children, preferring the inline-expansion cache and
   * falling back to a fetch when the folder was never expanded inline. Returns
   * null when the contents can't be loaded so the split can abort safely.
   */
  const getOrFetchExpansion = useCallback(
    async (
      filePath: string[],
      folderDriveId: string | null
    ): Promise<{ files: FileEntry[]; folders: FolderEntry[] } | null> => {
      const cached = mockTree.getExpansion(filePath, folderDriveId);
      if (cached && !cached.isLoading && !cached.error) {
        return { files: cached.files, folders: cached.folders };
      }
      try {
        const result = await fetchFiles({
          pathPrefix: filePath,
          maxDepth: 1,
          driveId: folderDriveId ?? undefined,
          excludeBlobKinds,
        });
        // The API echoes the parent folder; drop it so it isn't treated as a
        // child of itself.
        const parentPathKey = JSON.stringify(filePath);
        const childFolders = result.folders.filter(
          (f) => JSON.stringify(f.filePath) !== parentPathKey
        );
        return { files: result.fileList, folders: childFolders };
      } catch (err) {
        console.error("Failed to load folder for selection split:", err);
        return null;
      }
    },
    [mockTree, excludeBlobKinds]
  );

  /**
   * Split a folder selection to deselect a specific child item.
   * Removes the ancestor folder and adds all its children individually, except
   * the deselected item, loading folder contents on demand. Returns null if the
   * split cannot be performed (no selected ancestor, or contents failed to
   * load) — callers must leave the selection untouched in that case.
   */
  const splitFolderForDeselect = useCallback(
    (deselect: {
      type: "file" | "folder";
      fileId?: string;
      filePath: string[];
      driveId: string | null;
    }) =>
      computeFolderSplit({
        deselect,
        currentFileIds: core.selection.fileIds,
        currentFolderSelections: core.selection.folderSelections,
        getExpansion: getOrFetchExpansion,
      }),
    [
      core.selection.fileIds,
      core.selection.folderSelections,
      getOrFetchExpansion,
    ]
  );

  // Handle search result selection toggle
  const handleSearchResultSelect = useCallback(
    async (file: FileEntry) => {
      if (!isSelectableResult(file)) return;

      // Try to find the datum for this file in the tree.
      // If found and it's within a selected context (e.g. parent folder selected),
      // use toggleItemSelection to add a deselect/select action directly.
      // This preserves the parent folder structure in "Show selected" mode
      // instead of splitting the folder into individual items.
      const fileDatum = core.datums.find(
        (d) =>
          d.type === "file" &&
          d.payload?.type === "file" &&
          d.payload.fileId === file.fileId
      );
      if (fileDatum && core.isItemInSelectedContext(fileDatum.id)) {
        core.toggleItemSelection(fileDatum.id);
        return;
      }

      const fileDirectlySelected = core.selection.fileIds.includes(file.fileId);
      const folderInherited = isFolderSelected({
        filePath: file.filePath,
        driveId: file.driveId || null,
        documentDate: "",
      });

      if (folderInherited) {
        // Inherited from folder — split the ancestor folder to deselect this file
        const result = await splitFolderForDeselect({
          type: "file",
          fileId: file.fileId,
          filePath: file.filePath,
          driveId: file.driveId || null,
        });
        if (result) {
          core.setSelection(
            result.fileIds,
            result.folderPaths,
            result.metadata
          );
        }
      } else if (fileDirectlySelected) {
        // Directly selected only — simple removal
        const newFileIds = core.selection.fileIds.filter(
          (id) => id !== file.fileId
        );
        const folderPaths = folderSelectionsToFolderPaths(
          core.selection.folderSelections
        );
        core.setSelection(newFileIds, folderPaths);
      } else {
        // Not selected at all — select
        const metadata = new Map<string, PendingFileMetadata>([
          [file.fileId, { name: file.fileName, type: file.fileType }],
        ]);
        core.selectFiles([file.fileId], metadata);
      }
    },
    [
      core.datums,
      core.isItemInSelectedContext,
      core.toggleItemSelection,
      core.selection.fileIds,
      core.selection.folderSelections,
      core.setSelection,
      core.selectFiles,
      folderSelectionsToFolderPaths,
      isFolderSelected,
      splitFolderForDeselect,
    ]
  );

  // Handle search result folder selection toggle
  const handleSearchFolderSelect = useCallback(
    async (folder: FolderEntry) => {
      const folderPath = folder.driveId
        ? [folder.driveId, ...folder.filePath]
        : folder.filePath;
      const folderPathKey = JSON.stringify(folderPath);

      // Try to find the datum for this folder in the tree.
      // If found and in a selected context, use toggleItemSelection to preserve
      // the parent folder structure instead of splitting.
      const folderDatum = core.datums.find(
        (d) => d.type === "folder" && JSON.stringify(d.path) === folderPathKey
      );
      if (folderDatum && core.isItemInSelectedContext(folderDatum.id)) {
        core.toggleItemSelection(folderDatum.id);
        return;
      }

      const currentFolderPaths = folderSelectionsToFolderPaths(
        core.selection.folderSelections
      );
      const isDirectlySelected = currentFolderPaths.some(
        (p) => JSON.stringify(p) === folderPathKey
      );

      if (isDirectlySelected) {
        // Remove this folder from selection
        const newFolderPaths = currentFolderPaths.filter(
          (p) => JSON.stringify(p) !== folderPathKey
        );
        core.setSelection(core.selection.fileIds, newFolderPaths);
      } else if (isFolderSelected(folder)) {
        // Inherited from ancestor — split the ancestor folder
        const result = await splitFolderForDeselect({
          type: "folder",
          filePath: folder.filePath,
          driveId: folder.driveId,
        });
        if (result) {
          core.setSelection(
            result.fileIds,
            result.folderPaths,
            result.metadata
          );
        }
      } else {
        // Not selected — add to selection
        core.setSelection(core.selection.fileIds, [
          ...currentFolderPaths,
          folderPath,
        ]);
      }
    },
    [
      core.datums,
      core.isItemInSelectedContext,
      core.toggleItemSelection,
      core.selection.fileIds,
      core.selection.folderSelections,
      core.setSelection,
      folderSelectionsToFolderPaths,
      isFolderSelected,
      splitFolderForDeselect,
    ]
  );

  // Track whether initial selection has been applied
  const hasAppliedInitialRef = useRef(false);
  const initialFileIdsRef = useRef(initialFileIds);
  const initialFolderPathsRef = useRef(initialFolderPaths);

  // Update refs when initial values change (for re-applying on modal reopen)
  useEffect(() => {
    initialFileIdsRef.current = initialFileIds;
    initialFolderPathsRef.current = initialFolderPaths;
  }, [initialFileIds, initialFolderPaths]);

  // Apply initial selection once tree has loaded some data
  useEffect(() => {
    // Only apply initial selection once per mount
    if (hasAppliedInitialRef.current) return;

    // Wait until there are datums loaded
    const hasInitialSelection =
      initialFileIdsRef.current.length > 0 ||
      initialFolderPathsRef.current.length > 0;

    if (hasInitialSelection) {
      // Apply initial selection
      core.setSelection(
        initialFileIdsRef.current,
        initialFolderPathsRef.current
      );
      hasAppliedInitialRef.current = true;
    }
  }, [core.selection, core.setSelection]);

  // Lazy resolution: when "Show selected" is toggled on, or a selection is
  // pinned above the tree, resolve names for pending files that have empty
  // names (e.g., from programmatic/initial selection)
  const [isResolvingNames, setIsResolvingNames] = useState(false);
  const resolvingRef = useRef(false);
  useEffect(() => {
    if ((!showSelectedOnly && !pinned) || resolvingRef.current) return;

    // Find pending files with empty names
    const unnamed = core.selection.files.filter((f) => !f.name);
    if (unnamed.length === 0) return;

    resolvingRef.current = true;
    setIsResolvingNames(true);
    const unnamedIds = unnamed.map((f) => f.id);

    expandSelectionsToFileIds(unnamedIds, [])
      .then((result) => {
        const updates = new Map<string, PendingFileMetadata>();
        for (const r of result.files) {
          updates.set(r.fileId, { name: r.fileName, type: r.fileType || "" });
        }
        if (updates.size > 0) {
          core.updatePendingMetadata(updates);
        }
      })
      .catch((err) => {
        console.error("Failed to resolve pending file names:", err);
      })
      .finally(() => {
        resolvingRef.current = false;
        setIsResolvingNames(false);
      });
  }, [
    showSelectedOnly,
    pinned,
    core.selection.files,
    core.updatePendingMetadata,
  ]);

  // All datums in a selected context (unfiltered) — used for pending-file exclusion.
  // Uses isItemInSelectedContext so that deselected children of selected folders
  // are still included (they should be visible with unchecked checkboxes).
  const allSelectedDatums = useMemo(() => {
    if (!showSelectedOnly && !pinned) return [];
    return core.datums.filter(
      (datum) => datum.path.length > 0 && core.isItemInSelectedContext(datum.id)
    );
  }, [showSelectedOnly, pinned, core.datums, core.isItemInSelectedContext]);

  // Top-level selected datums for display in "Show selected" mode.
  // Excludes descendants whose ancestor is also selected to avoid duplicating
  // folder contents alongside the folder itself.
  const selectedDatums = useMemo(() => {
    if (allSelectedDatums.length === 0) return [];
    const selectedIds = new Set(allSelectedDatums.map((d) => d.id));
    return allSelectedDatums.filter((datum) => {
      let ancestor = datum.parent;
      while (ancestor && ancestor.path.length > 0) {
        if (selectedIds.has(ancestor.id)) return false;
        ancestor = ancestor.parent;
      }
      return true;
    });
  }, [allSelectedDatums]);

  // Get pending files (not in tree) for display in "Show selected" mode
  // Converts core.selection.files entries not in tree datums to FileEntry-like objects
  const pendingFilesForDisplay = useMemo((): FileEntry[] => {
    if (!showSelectedOnly && !pinned) return [];
    // Use allSelectedDatums (not the filtered selectedDatums) so that files
    // inside a selected folder are correctly excluded from the pending list
    const datumFileIds = new Set(
      allSelectedDatums
        .filter((d) => d.type === "file")
        .map((d) => {
          const payload = d.payload as { fileId?: string } | undefined;
          return payload?.fileId;
        })
        .filter((id): id is string => !!id)
    );
    // Return pending files that aren't in the tree, as minimal FileEntry objects
    // Filter out files with empty names (still resolving via lazy resolution)
    return core.selection.files
      .filter((f) => !datumFileIds.has(f.id) && f.name)
      .map((f) => ({
        taskId: "",
        fileId: f.id,
        fileName: f.name,
        filePath: [],
        fileType: f.type,
        status: "completed",
        isBlob: false,
        isPasswordProtected: false,
        hasStoredPassword: false,
        driveId: null,
        uploadedAt: null,
        documentDate: null,
      }));
  }, [showSelectedOnly, pinned, allSelectedDatums, core.selection.files]);

  // Get pending folders (not in tree) for display in "Show selected" mode
  // Converts core.selection.folderSelections entries not matched by tree datums to FolderEntry objects
  const pendingFoldersForDisplay = useMemo((): FolderEntry[] => {
    if (!showSelectedOnly && !pinned) return [];
    const datumFolderKeys = new Set(
      allSelectedDatums
        .filter((d) => d.type === "folder")
        .map((d) => JSON.stringify(d.path))
    );
    return core.selection.folderSelections
      .filter((fs) => {
        const fullPath = fs.driveId ? [fs.driveId, ...fs.path] : fs.path;
        return !datumFolderKeys.has(JSON.stringify(fullPath));
      })
      .map((fs) => ({
        filePath: fs.path,
        driveId: fs.driveId,
        documentDate: "",
      }));
  }, [
    showSelectedOnly,
    pinned,
    allSelectedDatums,
    core.selection.folderSelections,
  ]);

  // Handler for datum row click in "Show Selected" mode.
  // Folders: handled by onFolderToggle in SearchResultsView (inline expansion).
  // Files: toggle selection off (deselect).
  const handleDatumClick = useCallback(
    (datum: { id: string }) => {
      core.toggleItemSelection(datum.id);
    },
    [core.toggleItemSelection]
  );

  // Handler for datum checkbox click in "Show Selected" mode — always deselects.
  const handleDatumDeselect = useCallback(
    (datum: { id: string }) => {
      core.toggleItemSelection(datum.id);
    },
    [core.toggleItemSelection]
  );

  // Handler to deselect a pending file (not in tree)
  const handleDeselectFile = useCallback(
    (file: FileEntry) => {
      const newFileIds = core.selection.fileIds.filter(
        (id) => id !== file.fileId
      );
      const folderPaths = folderSelectionsToFolderPaths(
        core.selection.folderSelections
      );
      core.setSelection(newFileIds, folderPaths);
    },
    [
      core.selection.fileIds,
      core.selection.folderSelections,
      core.setSelection,
      folderSelectionsToFolderPaths,
    ]
  );

  // Auto-exit "show selected" mode when selection becomes empty
  useEffect(() => {
    if (showSelectedOnly && !core.selection.hasSelection) {
      setShowSelectedOnly(false);
    }
  }, [showSelectedOnly, core.selection.hasSelection]);

  // Handler to clear selection
  const handleClearSelection = useCallback(() => {
    core.clearSelection();
  }, [core.clearSelection]);

  // Handler to toggle select/deselect all visible search results
  const handleToggleSelectAllSearchResults = useCallback(() => {
    // Only consider processed files for selection
    const selectableFiles = searchResults.filter(isSelectableResult);
    if (selectableFiles.length === 0 && searchFolderResults.length === 0)
      return;

    const currentFolderPaths = folderSelectionsToFolderPaths(
      core.selection.folderSelections
    );

    const allFilesSelected =
      selectableFiles.length === 0 ||
      selectableFiles.every((file) =>
        core.selection.fileIds.includes(file.fileId)
      );
    const allFoldersSelected =
      searchFolderResults.length === 0 ||
      searchFolderResults.every((folder) => {
        const folderPath = folder.driveId
          ? [folder.driveId, ...folder.filePath]
          : folder.filePath;
        return currentFolderPaths.some(
          (p) => JSON.stringify(p) === JSON.stringify(folderPath)
        );
      });
    const allSelected = allFilesSelected && allFoldersSelected;

    if (allSelected) {
      // Deselect all search results (files and folders)
      const searchFileIds = new Set(searchResults.map((f) => f.fileId));
      const searchFolderKeys = new Set(
        searchFolderResults.map((folder) => {
          const folderPath = folder.driveId
            ? [folder.driveId, ...folder.filePath]
            : folder.filePath;
          return JSON.stringify(folderPath);
        })
      );
      const newFileIds = core.selection.fileIds.filter(
        (id) => !searchFileIds.has(id)
      );
      const newFolderPaths = currentFolderPaths.filter(
        (p) => !searchFolderKeys.has(JSON.stringify(p))
      );
      core.setSelection(newFileIds, newFolderPaths);
    } else {
      // Select all search results — files (only processed) and folders
      const metadata = new Map<string, PendingFileMetadata>();
      for (const file of selectableFiles) {
        metadata.set(file.fileId, {
          name: file.fileName,
          type: file.fileType,
        });
      }
      const allFileIds = selectableFiles.map((file) => file.fileId);
      const newFolderPaths = searchFolderResults.map((folder) =>
        folder.driveId ? [folder.driveId, ...folder.filePath] : folder.filePath
      );
      // Merge with existing selections
      const mergedFileIds = [
        ...new Set([...core.selection.fileIds, ...allFileIds]),
      ];
      const existingKeys = new Set(
        currentFolderPaths.map((p) => JSON.stringify(p))
      );
      const mergedFolderPaths = [
        ...currentFolderPaths,
        ...newFolderPaths.filter((p) => !existingKeys.has(JSON.stringify(p))),
      ];
      core.setSelection(mergedFileIds, mergedFolderPaths, metadata);
    }
  }, [
    searchResults,
    searchFolderResults,
    core.selection.fileIds,
    core.selection.folderSelections,
    core.setSelection,
    folderSelectionsToFolderPaths,
  ]);

  // All selected files — core now carries metadata for both tree and pending files
  const allSelectedFiles = core.selection.files;

  // Search bar component
  const searchBar = (
    <SearchBar
      ref={searchBarRef}
      onSearch={setSearchTerm}
      isSearching={isSearching}
    />
  );

  // When "Show Selected" is active with a search term, filter the selected
  // items to only those matching the search (case-insensitive substring).
  const searchLower = searchTerm.toLowerCase();
  const filteredSelectedDatums = useMemo(() => {
    if (!showSelectedOnly || !searchTerm) return selectedDatums;
    return selectedDatums.filter((d) =>
      (d.name ?? "").toLowerCase().includes(searchLower)
    );
  }, [selectedDatums, showSelectedOnly, searchTerm, searchLower]);

  const filteredPendingFiles = useMemo(() => {
    if (!showSelectedOnly || !searchTerm) return pendingFilesForDisplay;
    return pendingFilesForDisplay.filter((f) =>
      f.fileName.toLowerCase().includes(searchLower)
    );
  }, [pendingFilesForDisplay, showSelectedOnly, searchTerm, searchLower]);

  const filteredPendingFolders = useMemo(() => {
    if (!showSelectedOnly || !searchTerm) return pendingFoldersForDisplay;
    return pendingFoldersForDisplay.filter((f) => {
      const name = f.filePath[f.filePath.length - 1] ?? "";
      return name.toLowerCase().includes(searchLower);
    });
  }, [pendingFoldersForDisplay, showSelectedOnly, searchTerm, searchLower]);

  // The pinned section: the opening selection, as far as it is still
  // selected, in the same rows the "Show selected" view uses.
  const pinnedDatums = useMemo(
    () =>
      pinned
        ? selectedDatums.filter((d) => {
            if (d.type === "folder")
              return pinned.folderKeys.has(folderKey(d.path));
            const payload = d.payload as { fileId?: string } | undefined;
            return Boolean(
              payload?.fileId && pinned.fileIds.has(payload.fileId)
            );
          })
        : [],
    [pinned, selectedDatums]
  );
  const pinnedPendingFiles = useMemo(
    () =>
      pinned
        ? pendingFilesForDisplay.filter((f) => pinned.fileIds.has(f.fileId))
        : [],
    [pinned, pendingFilesForDisplay]
  );
  const pinnedPendingFolders = useMemo(
    () =>
      pinned
        ? pendingFoldersForDisplay.filter((f) =>
            pinned.folderKeys.has(
              folderKey(f.driveId ? [f.driveId, ...f.filePath] : f.filePath)
            )
          )
        : [],
    [pinned, pendingFoldersForDisplay]
  );
  const hasPinned =
    pinnedDatums.length > 0 ||
    pinnedPendingFiles.length > 0 ||
    pinnedPendingFolders.length > 0;

  const prepareForOpen = useCallback(
    (fileIds: string[], folderPaths: string[][]) => {
      hasAutoSwitchedRef.current = false;
      searchBarRef.current?.clear();
      setSearchTerm("");
      setShowSelectedOnly(false);
      core.setSelection(fileIds, folderPaths);
      setPinned(
        fileIds.length > 0 || folderPaths.length > 0
          ? {
              fileIds: new Set(fileIds),
              folderKeys: new Set(folderPaths.map(folderKey)),
            }
          : null
      );
    },
    [core.setSelection]
  );

  // Tree content - shows selected items, search results, or file tree
  const treeContent = showSelectedOnly ? (
    <SearchResultsView
      datums={filteredSelectedDatums}
      results={filteredPendingFiles}
      folderResults={filteredPendingFolders}
      isLoading={false}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSortChange={handleSortChange}
      onDatumClick={handleDatumClick}
      onDatumDeselect={handleDatumDeselect}
      onSearchFileDeselect={handleDeselectFile}
      onFileClick={handleSearchResultSelect}
      onFileSelect={handleSearchResultSelect}
      onFolderClick={handleSearchFolderSelect}
      showAcrossDrives={showAcrossDrives}
      driveIdToName={driveIdToName}
      showCheckboxes={true}
      isFileSelected={isFileSelected}
      isFolderSelected={isFolderSelected}
      isDatumSelected={core.isItemSelected}
      onFolderToggle={mockTree.toggleFolder}
      isFolderExpanded={mockTree.isExpanded}
      getFolderExpansion={mockTree.getExpansion}
      actionContext={pickerActionContext}
      narrowDate="hidden"
    />
  ) : isSearchActive ? (
    <SearchResultsView
      results={searchResults}
      folderResults={searchFolderResults}
      isLoading={isSearching}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSortChange={handleSortChange}
      onFileClick={handleSearchResultSelect}
      onFileSelect={handleSearchResultSelect}
      onFolderClick={handleSearchFolderSelect}
      showAcrossDrives={showAcrossDrives}
      driveIdToName={driveIdToName}
      showCheckboxes={true}
      isFileSelected={isFileSelected}
      isFolderSelected={isFolderSelected}
      onToggleSelectAll={handleToggleSelectAllSearchResults}
      onFolderToggle={mockTree.toggleFolder}
      isFolderExpanded={mockTree.isExpanded}
      getFolderExpansion={mockTree.getExpansion}
      actionContext={pickerActionContext}
      narrowDate="hidden"
    />
  ) : (
    <>
      {hasPinned && (
        <div data-testid="pinned-selection" className="mb-3">
          <div className="px-4 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Selected
          </div>
          <div className="max-h-56 overflow-auto">
            <SearchResultsView
              datums={pinnedDatums}
              results={pinnedPendingFiles}
              folderResults={pinnedPendingFolders}
              isLoading={false}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onDatumClick={handleDatumClick}
              onDatumDeselect={handleDatumDeselect}
              onSearchFileDeselect={handleDeselectFile}
              onFileClick={handleSearchResultSelect}
              onFileSelect={handleSearchResultSelect}
              onFolderClick={handleSearchFolderSelect}
              showAcrossDrives={showAcrossDrives}
              driveIdToName={driveIdToName}
              showCheckboxes={true}
              isFileSelected={isFileSelected}
              isFolderSelected={isFolderSelected}
              isDatumSelected={core.isItemSelected}
              onFolderToggle={mockTree.toggleFolder}
              isFolderExpanded={mockTree.isExpanded}
              getFolderExpansion={mockTree.getExpansion}
              actionContext={pickerActionContext}
              narrowDate="hidden"
              hideHeader
            />
          </div>
        </div>
      )}
      {core.tree}
    </>
  );

  // Combined view with search bar + tree/results
  const tree = (
    <>
      <div className="mb-4">{searchBar}</div>
      {treeContent}
    </>
  );

  // Selection controls component with "Show selected"/"Clear all" buttons
  const selectionControls =
    core.selection.fileCount > 0 || core.selection.folderCount > 0 ? (
      // min-w-0 + a truncating count: this row shares the modal footer
      // with Cancel/Start, and without a shrink path the count painted
      // under the buttons at phone (#11742 round 5 T5, Hunter).
      <div
        data-testid="selection-controls"
        className="flex min-w-0 items-center gap-3"
      >
        <button
          onClick={() => {
            const next = !showSelectedOnly;
            if (next) searchBarRef.current?.clear();
            setShowSelectedOnly(next);
          }}
          className={`text-sm transition-colors flex items-center gap-1.5 ${
            showSelectedOnly
              ? "text-blue-600 hover:text-blue-700"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Eye className="w-4 h-4" />
          {showSelectedOnly ? "Show all" : "Show selected"}
        </button>
        <button
          onClick={handleClearSelection}
          className="text-sm text-red-500 hover:text-red-600 transition-colors"
        >
          Clear all
        </button>
        <span className="min-w-0 truncate text-sm text-blue-600 font-medium">
          {core.selection.fileCount > 0 &&
            `${core.selection.fileCount} file${core.selection.fileCount !== 1 ? "s" : ""}`}
          {core.selection.fileCount > 0 &&
            core.selection.folderCount > 0 &&
            " + "}
          {core.selection.folderCount > 0 &&
            `${core.selection.folderCount} folder${core.selection.folderCount !== 1 ? "s" : ""}`}{" "}
          selected
        </span>
      </div>
    ) : null;

  return {
    tree,
    searchBar,
    treeContent,
    isSearchActive,
    selectedFileIds: core.selection.fileIds,
    selectedFiles: allSelectedFiles,
    selectedFolderSelections: core.selection.folderSelections,
    hasSelection: core.selection.hasSelection,
    selectedFileCount: core.selection.fileCount,
    selectedFolderCount: core.selection.folderCount,
    clearSelection: handleClearSelection,
    setSelection: core.setSelection,
    selectFiles: core.selectFiles,
    drives: core.drives,
    drivesEnabled: core.drivesEnabled,
    showSelectedOnly,
    setShowSelectedOnly,
    selectionControls,
    prepareForOpen,
  };
};
